import { useState } from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import {
  Box,
  Icons,
  Rating,
  Stack,
  Text,
  Button,
  styled,
  SxProps,
} from '@/theme/index';

type FeedbackProps = {
  feedbackFormUrl?: string;
  lightTheme?: boolean;
  removeStars?: boolean;
  sx?: SxProps;
};

export const Feedback = ({
  feedbackFormUrl,
  lightTheme,
  removeStars,
  sx,
}: FeedbackProps) => {
  const [ratingValue, setRatingValue] = useState(0);
  const [hasFeedback, setHasFeedback] = useState(Boolean(ratingValue));

  const { trackEvent } = useAppConfigContext();
  const {
    data: { brandName },
  } = useAppConstantsContext();

  const StyledButton = styled(Button)(({ theme }) => ({
    alignSelf: 'flex-start',
    borderRadius: '100px',
    borderColor: lightTheme
      ? theme.palette.primary.main
      : theme.palette.background.paper,
    color: lightTheme ? 'inherit' : theme.palette.primary.contrastText,

    '&:hover': {
      borderColor: lightTheme
        ? theme.palette.primary.main
        : theme.palette.background.paper,
    },
  }));

  const handleRatingClick = (
    _event: React.SyntheticEvent,
    newValue: number | null
  ) => {
    setRatingValue(newValue ?? 0);
    setHasFeedback(true);
    if (brandName === 'Bumper') {
      trackEvent('Bumper AI', 'Rating click', `${newValue} star`);
    }
  };

  const redirectToGoogleForm = () => {
    window.open(feedbackFormUrl, '_blank', 'noopener,noreferrer');
  };

  const starsComponent = hasFeedback ? (
    <Stack flexDirection="row" alignItems="center" mb={2.25}>
      <Box sx={{ color: 'success.light', mr: 1 }}>
        <Icons.CheckCircle color="inherit" fontSize="medium" />
      </Box>
      <Text>Thank you for your feedback!</Text>
    </Stack>
  ) : (
    <>
      <Text mb={1.5}>How would you rate this experience?</Text>
      <Stack flexDirection="row" mb={2.25}>
        <Text mt={0.5}>Poor</Text>
        <Rating
          sx={{ mx: 1.2 }}
          value={ratingValue}
          onChange={handleRatingClick}
          icon={
            <Box color="primary.dark">
              <Icons.StarRate fontSize="inherit" color="inherit" />
            </Box>
          }
          emptyIcon={
            <Box color={lightTheme ? '' : 'primary.contrastText'}>
              <Icons.StarBorder fontSize="inherit" />
            </Box>
          }
        />
        <Text mt={0.5}>Excellent</Text>
      </Stack>
    </>
  );

  return (
    <Stack
      flexDirection="column"
      color={lightTheme ? '' : 'primary.contrastText'}
      mt={2.5}
      sx={sx}
    >
      {!removeStars && starsComponent}
      <StyledButton
        variant="outlined"
        startIcon={<Icons.ChatBubbleOutline />}
        onClick={redirectToGoogleForm}
        color="inherit"
      >
        Share your thoughts
      </StyledButton>
    </Stack>
  );
};
